import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Loader2, WifiOff, FileText, HelpCircle, CheckCircle2, Send, Link as LinkIcon } from "lucide-react";
import { Toaster, toast } from "@/components/ui/sonner";
import DocumentRow from "@/components/DocumentRow";
import QuestionCard from "@/components/QuestionCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveToken = () => {
  const params = new URLSearchParams(window.location.search);
  let t = (params.get("c") || "").trim();
  if (!t) {
    const hm = (window.location.hash || "").match(/[?&#]c=([^&#]+)/);
    if (hm && hm[1]) t = decodeURIComponent(hm[1]).trim();
  }
  if (!t) {
    try { t = (sessionStorage.getItem("gb_c") || "").trim(); } catch (e) {}
  }
  if (t) { try { sessionStorage.setItem("gb_c", t); } catch (e) {} }
  return t;
};

const Header = ({ clientName, group }) => (
  <header className="px-5 pt-7 pb-6 bg-white border-b border-gray-100" data-testid="syam-header">
    {/* Greenbooks wordmark */}
    <div className="flex flex-col leading-none" data-testid="syam-wordmark">
      <span className="font-wordmark font-extrabold tracking-tight text-xl" style={{ color: "#0B3D2E" }}>
        GREENBOOKS
      </span>
      <span className="font-wordmark font-medium text-[10px] tracking-[0.32em] mt-1" style={{ color: "#16a34a" }}>
        CONSULTING&nbsp;INC.
      </span>
    </div>

    {/* Purpose banner */}
    <div className="mt-5">
      <p className="text-lg font-bold text-gray-900 leading-snug">
        Your monthly documents &amp; questions
      </p>
      <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">
        Upload your statements and answer any quick questions — takes a few minutes.
      </p>
    </div>

    {clientName && (
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <h1 className="text-base font-bold text-gray-900" data-testid="syam-greeting">
          Hi {clientName}
        </h1>
        {group && (
          <span className="text-[11px] font-medium rounded-full px-2.5 py-0.5 bg-green-50 text-green-700">
            Group {group}
          </span>
        )}
      </div>
    )}
  </header>
);

const SectionTitle = ({ icon: Icon, title, right }) => (
  <div className="flex items-center justify-between px-1 mb-3">
    <div className="flex items-center gap-2">
      <Icon className="w-5 h-5 text-[#16a34a]" />
      <h2 className="text-[15px] font-bold text-gray-900">{title}</h2>
    </div>
    {right}
  </div>
);

export default function SyamPortal() {
  const [status, setStatus] = useState("loading");
  const [clientName, setClientName] = useState("");
  const [group, setGroup] = useState("");
  const [documents, setDocuments] = useState([]);
  const [uploadedIds, setUploadedIds] = useState(new Set());
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [questionsSent, setQuestionsSent] = useState(false);
  const tokenRef = useRef("");

  useEffect(() => {
    const token = resolveToken();
    tokenRef.current = token;
    console.log("[SyamCPA] incoming ?c= token:", token || "(none)");
    if (!token) {
      setStatus("notoken");
      return;
    }
    axios
      .get(`${API}/syam/client`, { params: { c: token } })
      .then((res) => {
        console.log("[SyamCPA] token match found:", res.data.valid === true);
        if (!res.data.valid) {
          setStatus("invalid");
          return;
        }
        setClientName(res.data.clientName);
        setGroup(res.data.group || "");
        setDocuments(res.data.documents || []);
        setQuestions(res.data.questions || []);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  // group documents by period (newest first), received rows sink to bottom within group
  const grouped = useMemo(() => {
    const map = new Map();
    for (const d of documents) {
      const key = d.period || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    const periods = Array.from(map.keys()).sort().reverse();
    return periods.map((p) => ({
      period: p,
      docs: map.get(p).slice().sort((a, b) => {
        const au = uploadedIds.has(a.recordId) ? 1 : 0;
        const bu = uploadedIds.has(b.recordId) ? 1 : 0;
        return au - bu;
      }),
    }));
  }, [documents, uploadedIds]);

  const uploadedCount = uploadedIds.size;
  const totalDocs = documents.length;

  const answeredCount = useMemo(
    () => questions.filter((q) => answers[q.recordId]?.category).length,
    [questions, answers]
  );

  const updateAnswer = (recordId, patch) =>
    setAnswers((prev) => ({ ...prev, [recordId]: { ...(prev[recordId] || {}), ...patch } }));

  const submitQuestions = async () => {
    if (answeredCount < 1 || submitting) return;
    setSubmitting(true);
    const payload = {
      token: tokenRef.current,
      answers: questions
        .filter((q) => answers[q.recordId]?.category)
        .map((q) => {
          const a = answers[q.recordId];
          return {
            recordId: q.recordId,
            category: a.category,
            purpose: a.purpose || "",
            files: (a.files || []).map((f) => ({
              filename: f.filename,
              contentType: f.contentType,
              base64: f.base64,
            })),
          };
        }),
    };
    const attempt = () => axios.post(`${API}/syam/questions`, payload, { timeout: 60000 });
    try {
      let res;
      try {
        res = await attempt();
      } catch (e) {
        res = await attempt();
      }
      if (res?.data?.success) {
        toast.success("Answers sent to SyamCPA ✓", {
          description: `${res.data.count} question${res.data.count === 1 ? "" : "s"} answered.`,
        });
        setQuestionsSent(true);
      } else {
        throw new Error();
      }
    } catch (e) {
      toast.error("Couldn't send", { description: "Check your connection and tap again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white" data-testid="syam-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#16a34a]" />
        <p className="mt-3 text-sm text-gray-500">Loading your portal…</p>
      </div>
    );
  }

  if (status === "notoken") {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-8 text-center" data-testid="syam-notoken">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "#f0fdf4" }}>
          <LinkIcon className="w-7 h-7 text-[#16a34a]" />
        </div>
        <h2 className="mt-5 text-lg font-bold text-gray-900">Add your personal link</h2>
        <p className="mt-2 text-sm text-gray-600 max-w-xs">
          Open the link we emailed you — it ends with{" "}
          <span className="font-mono text-gray-800">…?c=YOURCODE</span> — to continue.
        </p>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-8 text-center" data-testid="syam-invalid">
        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-50 border border-gray-200">
          <WifiOff className="w-7 h-7 text-gray-400" />
        </div>
        <h2 className="mt-5 text-lg font-bold text-gray-900">This link isn't active</h2>
        <p className="mt-2 text-sm text-gray-600 max-w-xs">
          Please contact SyamCPA and we'll send you a fresh link.
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-8 text-center" data-testid="syam-error">
        <WifiOff className="w-10 h-10 text-gray-400" />
        <h2 className="mt-4 text-lg font-bold text-gray-900">We couldn't reach the server</h2>
        <p className="mt-2 text-sm text-gray-600 max-w-xs">
          Please check your connection and refresh the page.
        </p>
      </div>
    );
  }

  const hasQuestions = questions.length > 0 && !questionsSent;
  const nothingToDo = totalDocs === 0 && !hasQuestions;

  return (
    <div className="min-h-screen bg-[#f7f9f8] pb-28" data-testid="syam-portal">
      <Toaster position="top-center" richColors />
      <Header clientName={clientName} group={group} />

      <div className="px-4 pt-5 space-y-8">
        {nothingToDo && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100 mt-4">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#16a34a]" />
            <p className="mt-3 text-sm text-gray-600">
              You're all caught up — nothing to upload or answer right now.
            </p>
          </div>
        )}

        {/* DOCUMENTS */}
        {totalDocs > 0 && (
          <section data-testid="documents-section">
            <SectionTitle
              icon={FileText}
              title="Documents to upload"
              right={
                <span className="text-[12px] font-semibold text-[#16a34a]" data-testid="doc-count">
                  {uploadedCount} of {totalDocs} uploaded
                </span>
              }
            />
            <div className="space-y-5">
              {grouped.map((g) => (
                <div key={g.period}>
                  <p className="text-[12px] font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
                    {g.period}
                  </p>
                  <div className="space-y-3">
                    {g.docs.map((doc, i) => (
                      <DocumentRow
                        key={doc.recordId}
                        doc={doc}
                        token={tokenRef.current}
                        index={documents.indexOf(doc)}
                        onUploaded={(id) =>
                          setUploadedIds((prev) => new Set(prev).add(id))
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* QUESTIONS */}
        {hasQuestions && (
          <section data-testid="questions-section">
            <SectionTitle icon={HelpCircle} title="Questions to answer" />
            <div className="space-y-3">
              {questions.map((q, i) => (
                <QuestionCard
                  key={q.recordId}
                  item={q}
                  index={i}
                  answer={answers[q.recordId] || {}}
                  onChange={(patch) => updateAnswer(q.recordId, patch)}
                />
              ))}
            </div>
          </section>
        )}

        {questionsSent && questions.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center" data-testid="questions-sent">
            <CheckCircle2 className="w-8 h-8 mx-auto text-[#16a34a]" />
            <p className="mt-2 text-sm font-semibold text-green-800">
              Your answers went to SyamCPA. Thank you!
            </p>
          </div>
        )}
      </div>

      {/* fixed submit for questions */}
      {hasQuestions && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-4 pt-3 bg-gradient-to-t from-[#f7f9f8] via-[#f7f9f8] to-transparent" data-testid="questions-submit-bar">
          <button
            onClick={submitQuestions}
            disabled={answeredCount < 1 || submitting}
            data-testid="questions-submit-btn"
            className="w-full h-14 rounded-2xl font-bold text-white text-base flex items-center justify-center gap-2 shadow-lg transition-colors disabled:opacity-40 active:scale-[0.98]"
            style={{ backgroundColor: answeredCount < 1 ? "#9db8ab" : "#16a34a" }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="w-5 h-5" /> Send {answeredCount} answer{answeredCount === 1 ? "" : "s"}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
